// Sidebar Component - AgentBheem ERP
// Vue-inspired Modern Dark Theme with Indigo/Purple Accent
import { useState, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { useMenuStore } from '@/stores/menuStore'
import { useMenus } from '@/hooks/useMenu'
import { MenuIcon } from './MenuIcon'
import { SidebarItem } from './SidebarItem'
import { Search, X, Settings, LogOut } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/features/auth/store/authStore'
import type { MenuGroup } from '@/types/menu'

interface SidebarGroupProps {
  group: MenuGroup
  isCollapsed: boolean
}

function SidebarGroup({ group, isCollapsed }: SidebarGroupProps) {
  const { expandedGroupIds, toggleGroup } = useMenuStore()
  const isExpanded = expandedGroupIds.includes(group.id)

  return (
    <div className="mb-2">
      {/* Group Header - Clickable to expand/collapse */}
      {!isCollapsed ? (
        <button
          onClick={() => toggleGroup(group.id)}
          className={cn(
            'group flex w-full items-center justify-between rounded-xl px-3 py-3',
            'text-slate-400 transition-all duration-200',
            'hover:bg-slate-800/60 hover:text-slate-200',
            isExpanded && 'text-slate-200'
          )}
        >
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-xl transition-all duration-200',
                'bg-slate-800/80',
                'group-hover:bg-indigo-500/20 group-hover:shadow-lg group-hover:shadow-indigo-500/10',
                isExpanded && 'bg-indigo-500/20 shadow-lg shadow-indigo-500/10'
              )}
            >
              <MenuIcon
                name={group.name}
                className="h-4 w-4 text-indigo-400"
              />
            </div>
            <span className="text-sm font-medium">{group.name}</span>
          </div>
          <svg
            className={cn(
              'h-4 w-4 transition-all duration-300',
              'text-slate-500',
              isExpanded ? 'rotate-90 text-indigo-400' : ''
            )}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      ) : (
        <button
          onClick={() => toggleGroup(group.id)}
          className={cn(
            'group flex w-full items-center justify-center rounded-xl p-2',
            'transition-all duration-200',
            'hover:bg-slate-800/60',
            isExpanded && 'bg-slate-800/60'
          )}
          title={group.name}
        >
          <div
            className={cn(
              'flex h-9 w-9 items-center justify-center rounded-xl transition-all duration-200',
              'bg-slate-800/80',
              'group-hover:bg-indigo-500/20 group-hover:shadow-lg group-hover:shadow-indigo-500/10',
              isExpanded && 'bg-indigo-500/20 shadow-lg shadow-indigo-500/10'
            )}
          >
            <MenuIcon
              name={group.name}
              className="h-4 w-4 text-indigo-400"
            />
          </div>
        </button>
      )}

      {/* Group Items - Only show when expanded */}
      {isExpanded && (
        <ul className={cn(
          'mt-1 space-y-1',
          !isCollapsed && 'ml-6 pl-3 border-l-2 border-slate-700/50'
        )}>
          {group.items.map((item) => (
            <li key={item.id}>
              <SidebarItem item={item} isCollapsed={isCollapsed} />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function Sidebar() {
  const navigate = useNavigate()
  const { isCollapsed, toggleCollapse } = useMenuStore()
  const { logout } = useAuthStore()
  const { data: menus } = useMenus()
  const [searchQuery, setSearchQuery] = useState('')

  const handleLogout = () => {
    logout()
    navigate('/auth/login')
  }

  const handleSettings = () => {
    navigate('/settings')
  }

  // Filter menus based on search query and exclude Project Management (handled by PM popup)
  const filteredMenus = useMemo(() => {
    if (!menus) return menus

    // Filter out Project Management group - it's now in the header PM popup
    const excludePM = (group: MenuGroup): boolean => {
      return group.name.toLowerCase() !== 'project management'
    }

    const query = searchQuery.toLowerCase().trim()

    if (!query) {
      // No search - just exclude PM
      return {
        grouped: menus.grouped?.filter(excludePM) || [],
        ungrouped: menus.ungrouped || [],
      }
    }

    const filterGroup = (group: MenuGroup): MenuGroup | null => {
      const filteredItems = group.items.filter(
        (item) =>
          item.name.toLowerCase().includes(query) ||
          item.children?.some((child) => child.name.toLowerCase().includes(query))
      )

      if (filteredItems.length > 0 || group.name.toLowerCase().includes(query)) {
        return { ...group, items: filteredItems.length > 0 ? filteredItems : group.items }
      }
      return null
    }

    const filteredGrouped = menus.grouped
      ?.filter(excludePM)
      .map(filterGroup)
      .filter((g): g is MenuGroup => g !== null)

    const filteredUngrouped = menus.ungrouped?.filter((item) =>
      item.name.toLowerCase().includes(query)
    )

    return {
      grouped: filteredGrouped || [],
      ungrouped: filteredUngrouped || [],
    }
  }, [menus, searchQuery])

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 bottom-0 z-40',
        'flex flex-col',
        'bg-gradient-to-b from-[#0f172a] to-[#0c1222]',
        'border-r border-white/[0.08]',
        'transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]',
        'hidden lg:flex',
        'shadow-[4px_0_24px_rgba(0,0,0,0.25)]',
        isCollapsed ? 'w-[72px]' : 'w-[280px]'
      )}
    >
      {/* Header Section - matches main header height */}
      <div
        className={cn(
          'flex items-center h-[70px]',
          isCollapsed ? 'justify-center px-2' : 'justify-between px-4'
        )}
      >
        {/* Logo and Brand */}
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'flex items-center justify-center rounded-xl p-1.5',
              'bg-gradient-to-br from-indigo-500 to-purple-600',
              'shadow-lg shadow-indigo-500/40',
              'h-10 w-10'
            )}
          >
            <img
              src="/logo.png"
              alt="AgentBheem"
              className="h-full w-full object-contain"
            />
          </div>
          {!isCollapsed && (
            <div className="flex flex-col">
              <span className="text-lg font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
                AgentBheem
              </span>
              <span className="text-[9px] text-slate-500 uppercase tracking-widest font-medium">
                Enterprise Resource Planning
              </span>
            </div>
          )}
        </div>

        {/* Toggle Button */}
        {!isCollapsed && (
          <button
            onClick={toggleCollapse}
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-lg',
              'bg-slate-800/80 border border-white/[0.08]',
              'text-slate-400 transition-all duration-200',
              'hover:bg-indigo-500/20 hover:text-indigo-400 hover:border-indigo-500/30'
            )}
            title="Collapse sidebar"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 18l-6-6 6-6" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 18l-6-6 6-6" />
            </svg>
          </button>
        )}
      </div>

      {/* Search Bar */}
      {!isCollapsed ? (
        <div className="px-3 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search menus..."
              className={cn(
                'w-full pl-9 pr-8 py-2.5 rounded-xl',
                'bg-slate-800/50 border border-white/[0.08]',
                'text-sm text-slate-200 placeholder-slate-500',
                'focus:outline-none focus:border-indigo-500/50 focus:ring-2 focus:ring-indigo-500/20',
                'transition-all duration-200'
              )}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-slate-500 hover:text-slate-300 hover:bg-slate-700/50 transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="py-3 px-2">
          <button
            onClick={() => toggleCollapse()}
            className={cn(
              'flex w-full items-center justify-center p-2.5 rounded-xl',
              'bg-slate-800/50 border border-white/[0.08]',
              'text-slate-500 transition-all duration-200',
              'hover:bg-indigo-500/20 hover:text-indigo-400 hover:border-indigo-500/30'
            )}
            title="Expand to search"
          >
            <Search className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Navigation */}
      <nav className="relative flex-1 overflow-y-auto px-3 py-4 scrollbar-thin">
        {filteredMenus?.grouped && filteredMenus.grouped.length > 0 ? (
          <div className="space-y-2">
            {searchQuery && !isCollapsed && (
              <p className="px-3 py-2 text-xs text-slate-500">
                {filteredMenus.grouped.length} result{filteredMenus.grouped.length !== 1 ? 's' : ''}{' '}
                found
              </p>
            )}

            {filteredMenus.grouped
              .sort((a, b) => a.position - b.position)
              .map((group) => (
                <SidebarGroup key={group.id} group={group} isCollapsed={isCollapsed} />
              ))}

            {filteredMenus.ungrouped && filteredMenus.ungrouped.length > 0 && (
              <div className="mt-6 pt-4 border-t border-white/[0.08]">
                {!isCollapsed && (
                  <div className="flex items-center gap-2 px-3 mb-3">
                    <div className="h-px flex-1 bg-gradient-to-r from-indigo-500/20 to-transparent" />
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      More
                    </p>
                    <div className="h-px flex-1 bg-gradient-to-l from-indigo-500/20 to-transparent" />
                  </div>
                )}
                <ul className="space-y-1">
                  {filteredMenus.ungrouped.map((item) => (
                    <li key={item.id}>
                      <SidebarItem item={item} isCollapsed={isCollapsed} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center mb-3">
              <Search className="h-6 w-6 text-indigo-400" />
            </div>
            {!isCollapsed && (
              <>
                {searchQuery ? (
                  <>
                    <p className="text-sm text-slate-300">No results found</p>
                    <p className="text-xs text-slate-500 mt-1">Try a different search term</p>
                    <button
                      onClick={() => setSearchQuery('')}
                      className="mt-3 px-3 py-1.5 text-xs text-indigo-400 hover:text-indigo-300 bg-indigo-500/10 hover:bg-indigo-500/20 rounded-lg transition-colors"
                    >
                      Clear search
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-slate-300">No menus available</p>
                    <p className="text-xs text-slate-500 mt-1">Check your permissions</p>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </nav>

      {/* Bottom Section */}
      <div className="border-t border-white/[0.08] p-3 bg-gradient-to-t from-black/20 to-transparent">
        <div className="space-y-2">
          {/* Settings */}
          <button
            onClick={handleSettings}
            className={cn(
              'group flex w-full items-center gap-3 rounded-xl px-3 py-3',
              'text-slate-400 transition-all duration-200',
              'hover:bg-slate-800/60 hover:text-slate-200',
              isCollapsed && 'justify-center'
            )}
          >
            <div
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-xl transition-all duration-200',
                'bg-slate-800/80',
                'group-hover:bg-indigo-500/20 group-hover:shadow-lg group-hover:shadow-indigo-500/10'
              )}
            >
              <Settings className="h-4 w-4 text-indigo-400" />
            </div>
            {!isCollapsed && <span className="text-sm font-medium">Settings</span>}
          </button>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className={cn(
              'group flex w-full items-center gap-3 rounded-xl px-3 py-3',
              'text-red-400 transition-all duration-200',
              'hover:bg-slate-800/60',
              isCollapsed && 'justify-center'
            )}
          >
            <div
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-xl transition-all duration-200',
                'bg-slate-800/80',
                'group-hover:bg-red-500/20 group-hover:shadow-lg group-hover:shadow-red-500/10'
              )}
            >
              <LogOut className="h-4 w-4 text-red-400" />
            </div>
            {!isCollapsed && <span className="text-sm font-medium">Logout</span>}
          </button>
        </div>

        {/* Expand button when collapsed */}
        {isCollapsed && (
          <button
            onClick={toggleCollapse}
            className={cn(
              'flex w-full items-center justify-center mt-2 p-2.5 rounded-xl',
              'bg-gradient-to-r from-indigo-500/10 to-purple-500/5',
              'border border-white/[0.08]',
              'text-slate-400 transition-all duration-200',
              'hover:from-indigo-500/20 hover:to-purple-500/10',
              'hover:text-slate-200 hover:border-indigo-500/20'
            )}
            title="Expand sidebar"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 6l6 6-6 6" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 6l6 6-6 6" />
            </svg>
          </button>
        )}
      </div>
    </aside>
  )
}
